import { ENCHANTMENTS } from '../data/enchantments';
import { getBaseItemName, getItemTemplate } from '../data/items';
import { buildAffixDerivedName, inferGrammar } from '../data/itemNaming';
import { WeaponSubtype, ArmorSubtype } from '../types/item';
import type { RolledAffixes } from '../types/item';
import { EquipmentSlot } from '../constants';

/**
 * Devuelve el nombre a mostrar de un item.
 *
 * Prioridad de composición (ver docs/ITEM_NAMING.md):
 *  1. Si el item tiene afijos rolled (RARE/EPIC) → nombre DERIVADO de sus afijos
 *     (prefijo adjetivo concordado + base + sufijo). El encantamiento, si lo hay,
 *     NO decora el nombre (queda visible en el tooltip de stats) para no encadenar
 *     nombres kilométricos.
 *  2. Si no hay afijos pero sí `enchantId` → prefijo/sufijo del encantamiento (legacy).
 *  3. Si no hay ni afijos ni enchant → nombre base.
 *
 * `rolledRarity` se acepta por conveniencia/compat pero no cambia la lógica: lo que
 * decide el nombre derivado es la presencia de `rolledAffixes`.
 */
export const formatItemName = (
  itemId: string,
  enchantId?: string | null,
  _rolledRarity?: number,
  rolledAffixes?: RolledAffixes | null
): string => {
  const base = getBaseItemName(itemId);

  if (rolledAffixes && Object.keys(rolledAffixes).length > 0) {
    const template = getItemTemplate(itemId);
    const grammar = template?.grammar ?? inferGrammar(base);
    return buildAffixDerivedName(base, rolledAffixes, grammar);
  }

  if (!enchantId) return base;

  const enchant = ENCHANTMENTS[enchantId];
  if (!enchant) return base;

  const prefix = enchant.prefix ? `${enchant.prefix} ` : '';
  const suffix = enchant.suffix ? ` ${enchant.suffix}` : '';
  return `${prefix}${base}${suffix}`.trim();
};

/**
 * Obtiene el nombre legible del subtipo de un item
 */
export function getSubtypeDisplayName(subtype: string | undefined): string | null {
  if (!subtype) return null;

  const subtypeNames: Record<string, string> = {
    // Subtipos de armas
    [WeaponSubtype.DAGGER]: 'Daga',
    [WeaponSubtype.AXE]: 'Hacha',
    [WeaponSubtype.MACE]: 'Maza',
    [WeaponSubtype.SWORD]: 'Espada',
    [WeaponSubtype.STAFF]: 'Báculo',
    [WeaponSubtype.WAND]: 'Varita',
    [WeaponSubtype.KNUCKLES]: 'Puños',
    [WeaponSubtype.BOW]: 'Arco',
    [WeaponSubtype.CROSSBOW]: 'Ballesta',
    [WeaponSubtype.THROWABLE]: 'Arma Arrojadiza',
    // Subtipos de armadura
    [ArmorSubtype.HEAVY]: 'Pesado',
    [ArmorSubtype.LIGHT]: 'Liviano',
    [ArmorSubtype.CLOTH]: 'Tela',
  };

  return subtypeNames[subtype] || subtype;
}

/**
 * Obtiene el nombre legible (español) de un slot de equipamiento.
 * Devuelve `null` si el slot es undefined o desconocido.
 */
export function getSlotDisplayName(slot: string | undefined): string | null {
  if (!slot) return null;

  const slotNames: Record<string, string> = {
    [EquipmentSlot.WEAPON]: 'Arma',
    [EquipmentSlot.BODY]: 'Cuerpo',
    [EquipmentSlot.HEAD]: 'Casco',
    [EquipmentSlot.SHIELD]: 'Escudo',
    [EquipmentSlot.BOOTS]: 'Botas',
    [EquipmentSlot.RING]: 'Anillo',
    [EquipmentSlot.AMULET]: 'Amuleto',
    [EquipmentSlot.BACK]: 'Capa',
    [EquipmentSlot.BOAT]: 'Barco',
  };

  return slotNames[slot] ?? null;
}

/**
 * Obtiene el nombre legible (español) de la empuñadura de un arma.
 */
export function getWeaponHandDisplayName(hand: 'one' | 'two' | undefined): string | null {
  if (hand === 'one') return 'Una mano';
  if (hand === 'two') return 'Dos manos';
  return null;
}
